/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from "vue";
import Router from "vue-router";
import { firebase } from "@/firebase/firebaseConfig.js";
import Store from "@/store/store";
import authService from "@/services/authService";
import moment from "moment";
const FREE_PATHS = ["/login/enterprise"];

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 };
  },
  routes: [
    // -- トップへリダイレクト
    {
      path: "/",
      redirect: (_) => {
        return "/enterprise/live-map";
      },
    },

    // -- Login
    {
      path: "/login",
      name: "Login",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        {
          path: "enterprise",
          name: "login-enterprise",
          component: () => import("@/views/enterprise/Login.vue"),
        },
        {
          path: "enterprise/:enterpriseId",
          name: "login-enterprise-params",
          component: () => import("@/views/enterprise/Login.vue"),
        },
      ],
    },

    /*=========================================================================================
       法人
    ==========================================================================================*/
    // Map
    {
      path: "/enterprise",
      name: "Enterprise",
      component: () => import("@/layouts/full-page/FullPage.vue"),
      children: [
        {
          path: "map-view",
          name: "enterprise-map-view",
          component: () => import("@/components/pages/MapView.vue"),
          meta: {
            no_scroll: true,
          },
        },
      ],
    },
    {
      path: "/enterprise",
      name: "Enterprise",
      component: () => import("./layouts/main/Main.vue"),
      children: [
        {
          path: "dashboard",
          name: "enterprise-dashboard",
          component: () => import("./views/enterprise/Dashboard.vue"),
        },
        // Map
        {
          path: "live-map",
          name: "enterprise-live-map",
          component: () => import("./views/enterprise/live-map/LiveMap.vue"),
          meta: {
            no_scroll: true,
          },
        },
        {
          path: "rg-object-history-player",
          name: "enterprise-rg-object-history-player",
          component: () =>
            import(
              "@/views/enterprise/rg-object-history/RgObjectHistoryPlayer.vue"
            ),
          meta: {
            no_scroll: true,
          },
        },
        // Plot
        {
          path: "plot-edit",
          name: "enterprise-plot-edit",
          component: () =>
            import("./views/enterprise/management/plot/Plot.vue"),
        },
        // Plot List
        {
          path: "plot-list",
          name: "enterprise-plot-list",
          component: () =>
            import("./views/enterprise/management/plot/PlotList.vue"),
          meta: {
            breadcrumb: [
              {
                title: "プロット",
              },
              { title: "プロット一覧", active: true },
            ],
            pageTitle: "プロット一覧",
          },
        },
        // Private Plot
        {
          path: "private-plot-edit",
          name: "enterprise-private-plot-edit",
          component: () =>
            import("./views/enterprise/management/privatePlot/Plot.vue"),
        },
        // PrivatePlot List
        {
          path: "private-plot-list",
          name: "enterprise-private-plot-list",
          component: () =>
            import("./views/enterprise/management/privatePlot/PlotList.vue"),
          meta: {
            breadcrumb: [
              {
                title: "プライベートプロット",
              },
              { title: "プライベートプロット一覧", active: true },
            ],
            pageTitle: "プライベートプロット一覧",
          },
        },

        /*=========================================================================================
          ツール -> 航海記録
        ==========================================================================================*/
        {
          path: "voyage-histories",
          name: "voyage-histories",
          component: () =>
            import("@/views/voyage-history/VoyageHistoryList.vue"),
          meta: {
            breadcrumb: [
              {
                title: "航海記録",
              },
              { title: "航海記録一覧", active: true },
            ],
            pageTitle: "航海記録一覧",
          },
        },
        {
          path: "voyage-history-player",
          name: "voyage-history-player",
          component: () =>
            import("@/views/voyage-history/VoyageHistoryPlayer.vue"),
          meta: {
            breadcrumb: [
              {
                title: "航海記録",
              },
              { title: "航海記録一覧", url: { name: "voyage-histories" } },
              { title: "航海記録再生", active: true },
            ],
            pageTitle: "航海記録再生",
          },
        },
        /*=========================================================================================
          法人 -> 管理 -> 社員管理系
        ==========================================================================================*/
        {
          path: "management/user-list",
          name: "enterprise-management-user-list",
          component: () =>
            import("./views/enterprise/management/user/user-list/UserList.vue"),
          meta: {
            breadcrumb: [
              {
                title: "社員管理",
              },
              { title: "社員一覧", active: true },
            ],
            pageTitle: "社員一覧",
          },
        },
        {
          path: "management/user-view/:userId",
          name: "enterprise-management-user-view",
          component: () =>
            import("./views/enterprise/management/user/user-view/UserView.vue"),
          meta: {
            breadcrumb: [
              {
                title: "社員管理",
              },
              {
                title: "社員一覧",
                url: { name: "enterprise-management-user-list" },
              },
              { title: "社員詳細", active: true },
            ],
            parent: "enterprise-management-user-list",
            pageTitle: "社員詳細",
          },
        },
        {
          path: "management/user-edit/:userId",
          name: "enterprise-management-user-edit",
          component: () =>
            import("./views/enterprise/management/user/user-edit/UserEdit.vue"),
          meta: {
            breadcrumb: [
              {
                title: "社員管理",
              },
              {
                title: "社員一覧",
                url: { name: "enterprise-management-user-list" },
              },
              {
                title: "社員詳細",
                url: { name: "enterprise-management-user-view" },
              },
              { title: "社員編集", active: true },
            ],
            parent: "enterprise-management-user-view",
            pageTitle: "社員編集",
          },
        },
        {
          path: "management/user-add",
          name: "enterprise-management-user-add",
          component: () =>
            import("./views/enterprise/management/user/user-add/UserAdd.vue"),
          meta: {
            breadcrumb: [
              {
                title: "社員管理",
              },
              {
                title: "社員一覧",
                url: { name: "enterprise-management-user-list" },
              },
              { title: "社員追加", active: true },
            ],
            parent: "enterprise-management-user-list",
            pageTitle: "社員追加",
          },
        },
        {
          path: "management/user-sort",
          name: "enterprise-management-user-sort",
          component: () =>
            import("./views/enterprise/management/user/user-list/UserSort.vue"),
          meta: {
            breadcrumb: [
              {
                title: "社員管理",
              },
              {
                title: "社員一覧",
                url: { name: "enterprise-management-user-list" },
              },
              { title: "社員表示順序", active: true },
            ],
            parent: "enterprise-management-user-list",
            pageTitle: "社員表示順序",
          },
        },
        {
          path: "management/license-add/:userId",
          name: "enterprise-management-license-add",
          component: () =>
            import(
              "./views/enterprise/management/user/license-add/LicenseAdd.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "社員管理",
              },
              {
                title: "社員一覧",
                url: { name: "enterprise-management-user-list" },
              },
              {
                title: "社員詳細",
                url: { name: "enterprise-management-user-view" },
              },
              { title: "ライセンス追加", active: true },
            ],
            parent: "enterprise-management-user-view",
            pageTitle: "ライセンス追加",
          },
        },
        {
          path: "management/license-edit",
          name: "enterprise-management-license-edit",
          component: () =>
            import(
              "./views/enterprise/management/user/license-edit/LicenseEdit"
            ),
        },

        /*=========================================================================================
          法人 -> 管理 -> 船舶管理系
        ==========================================================================================*/
        {
          path: "management/vessel-list",
          name: "enterprise-management-vessel-list",
          component: () =>
            import(
              "./views/enterprise/management/vessels/vessel-list/VesselList.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "船舶管理",
              },
              { title: "船舶一覧", active: true },
            ],
            pageTitle: "船舶一覧",
          },
        },
        {
          path: "management/vessel-view/:vesselId",
          name: "enterprise-management-vessel-view",
          component: () =>
            import(
              "./views/enterprise/management/vessels/vessel-view/VesselView.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "船舶管理",
              },
              {
                title: "船舶一覧",
                url: { name: "enterprise-management-vessel-list" },
              },
              {
                title: "船舶詳細",
                active: true,
              },
            ],
            parent: "enterprise-management-vessel-list",
            pageTitle: "船舶詳細",
          },
        },
        {
          path: "management/vessel-edit/:vesselId",
          name: "enterprise-management-vessel-edit",
          component: () =>
            import(
              "./views/enterprise/management/vessels/vessel-edit/VesselEdit.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "船舶管理",
              },
              {
                title: "船舶一覧",
                url: { name: "enterprise-management-vessel-list" },
              },
              {
                title: "船舶詳細",
                url: { name: "enterprise-management-vessel-view" },
              },
              { title: "船舶編集", active: true },
            ],
            parent: "enterprise-management-vessel-view",
            pageTitle: "船舶編集",
          },
        },
        {
          path: "management/vessel-sort",
          name: "enterprise-management-vessel-sort",
          component: () =>
            import(
              "./views/enterprise/management/vessels/vessel-list/VesselSort.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "船舶管理",
              },
              {
                title: "船舶一覧",
                url: { name: "enterprise-management-vessel-list" },
              },
              { title: "船舶表示順序", active: true },
            ],
            parent: "enterprise-management-vessel-list",
            pageTitle: "船舶表示順序",
          },
        },

        /*=========================================================================================
          法人 -> 管理 -> カメラ管理系(平面進捗図)
        ==========================================================================================*/
        {
          path: "management/camera-list",
          name: "enterprise-management-camera-list",
          component: () =>
            import(
              "./views/enterprise/management/camera/camera-list/CameraList.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "カメラ管理",
              },
              {
                title: "カメラ一覧",
                active: true,
              },
            ],
            pageTitle: "カメラ一覧",
          },
        },
        {
          path: "management/camera-sort",
          name: "enterprise-management-camera-sort",
          component: () =>
            import(
              "./views/enterprise/management/camera/camera-list/CameraSort.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "カメラ管理",
              },
              {
                title: "カメラ一覧",
                url: { name: "enterprise-management-camera-list" },
              },
              { title: "カメラ表示順序", active: true },
            ],
            parent: "enterprise-management-camera-list",
            pageTitle: "カメラ表示順序",
          },
        },
        {
          path: "management/camera-view/:cameraId/realtime",
          name: "enterprise-management-camera-view-realtime",
          component: () =>
            import(
              "./views/enterprise/management/camera/camera-view/CameraViewRealtime.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "カメラ管理",
              },
              {
                title: "カメラ一覧",
                url: { name: "enterprise-management-camera-list" },
              },
              {
                title: "リアルタイムカメラ",
              },
              {
                title: "",
                active: true,
              },
            ],
            parent: "enterprise-management-camera-list",
            pageTitle: "リアルタイムカメラ",
          },
        },
        {
          path: "management/camera-view/:cameraId/history",
          name: "enterprise-management-camera-view-history",
          component: () =>
            import(
              "./views/enterprise/management/camera/camera-view/CameraViewHistory.vue"
            ),
          meta: {
            breadcrumb: [
              {
                title: "カメラ管理",
              },
              {
                title: "カメラ一覧",
                url: { name: "enterprise-management-camera-list" },
              },
              {
                title: "航海履歴",
              },
              {
                title: "",
                active: true,
              },
            ],
            parent: "enterprise-management-camera-list",
            pageTitle: "航海履歴",
          },
        },

        /*=========================================================================================
         法人 -> 管理 -> お知らせ一覧
        ==========================================================================================*/
        {
          path: "management/notification-list",
          name: "enterprise-management-notification-list",
          component: () => import("./views/notification-list/NotificationList"),
          meta: {
            breadcrumb: [
              {
                title: "お知らせ管理",
              },
              {
                title: "お知らせ一覧",
                active: true,
              },
            ],
            pageTitle: "お知らせ一覧",
          },
        },
        /*=========================================================================================
         通知
        ==========================================================================================*/
        {
          path: "notification-list",
          redirect: "notification-list/all",
          name: "notification-list-all",
        },
        {
          path: "notification-list/:filter",
          name: "notification-list",
          component: () => import("./views/notification-list/NotificationList"),
          meta: {
            breadcrumb: [
              {
                title: "通知管理",
              },
              {
                title: "通知一覧",
                active: true,
              },
            ],
            pageTitle: "通知一覧",
          },
        },
        /*=========================================================================================
          機能拡張 -> PortMaster
         ==========================================================================================*/
        // 入出港管理
        {
          path: "addon/port-master-port-menu",
          name: "addon-port-master-port-menu",
          component: () =>
            import("./views/enterprise/addon/port-master/port-menu/PortMenu"),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                active: true,
              },
            ],
            pageTitle: "入出港管理",
            portMasterNavigation: true,
          },
        },

        // 港湾選択
        {
          path: "addon/port-master-port-select",
          name: "addon-port-master-port-select",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/port-select/PortSelect"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "港湾選択",
                active: true,
              },
            ],
            pageTitle: "港湾選択",
            portMasterNavigation: true,
          },
        },

        // 船舶管理
        {
          path: "addon/port-master-vessel-master",
          name: "addon-port-master-vessel-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/vessel-master/VesselMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "船舶管理一覧",
                active: true,
              },
            ],
            pageTitle: "船舶管理",
            portMasterNavigation: true,
          },
        },
        // 船舶新規登録
        {
          path: "addon/port-master-vessel-insert",
          name: "addon-port-master-vessel-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/vessel-master/VesselInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "船舶管理一覧",
                url: { name: "addon-port-master-vessel-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "船舶管理",
            portMasterNavigation: true,
          },
        },
        // 船舶情報
        {
          path: "addon/port-master-vessel-view/:itemId",
          name: "addon-port-master-vessel-view",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/vessel-master/VesselViewData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "船舶管理一覧",
                url: { name: "addon-port-master-vessel-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "船舶情報",
                active: true,
              },
            ],
            pageTitle: "船舶情報",
            portMasterNavigation: true,
          },
        },

        // バース管理
        {
          path: "addon/port-master-berth-master",
          name: "addon-port-master-berth-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/berth-master/BerthMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "バース管理一覧",
                active: true,
              },
            ],
            pageTitle: "バース管理",
            portMasterNavigation: true,
          },
        },
        // バース新規登録
        {
          path: "addon/port-master-berth-insert",
          name: "addon-port-master-berth-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/berth-master/BerthInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "バース管理一覧",
                url: { name: "addon-port-master-berth-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "バース管理",
            portMasterNavigation: true,
          },
        },
        // バース情報
        {
          path: "addon/port-master-berth-view/:itemId",
          name: "addon-port-master-berth-view",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/berth-master/BerthViewData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "バース管理一覧",
                url: { name: "addon-port-master-berth-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "バース情報",
                active: true,
              },
            ],
            pageTitle: "バース情報",
            portMasterNavigation: true,
          },
        },

        // 法人代理店管理
        {
          path: "addon/port-master-enterprise-agency-master",
          name: "addon-port-master-enterprise-agency-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/enterprise-agency-master/EnterpriseAgencyMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "代理店（法人）管理一覧",
                active: true,
              },
            ],
            pageTitle: "代理店（法人）管理",
            portMasterNavigation: true,
          },
        },
        // 法人代理店新規登録
        {
          path: "addon/port-master-enterprise-agency-insert",
          name: "addon-port-master-enterprise-agency-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/enterprise-agency-master/EnterpriseAgencyInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "代理店（法人）管理一覧",
                url: { name: "addon-port-master-enterprise-agency-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "代理店（法人）管理",
            portMasterNavigation: true,
          },
        },
        // 法人代理店情報
        {
          path: "addon/port-master-enterprise-agency-view/:itemId",
          name: "addon-port-master-enterprise-agency-view",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/enterprise-agency-master/EnterpriseAgencyViewData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "代理店（法人）管理一覧",
                url: { name: "addon-port-master-enterprise-agency-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "代理店（法人）情報",
                active: true,
              },
            ],
            pageTitle: "代理店（法人）情報",
            portMasterNavigation: true,
          },
        },

        // 入出港一覧表示
        {
          path: "addon/port-master/destination-work-display/:displayId",
          name: "addon-port-master-destination-work-display",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/destination-work/DestinationWorkList"
            ),
          meta: {
            isPortMasterFullScreen: true,
          },
        },
        // 入出港一覧管理
        {
          path: "addon/port-master/destination-work-edit/",
          name: "addon-port-master-destination-work-edit",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/destination-work-edit/DestinationWorkEdit"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "入出港予定表",
                url: { name: "addon-port-master-destination-order-list" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "編集",
                active: true,
              },
            ],
            pageTitle: "入出港予定表",
            portMasterNavigation: true,
          },
        },
        // 入出港一覧管理 項目順序変更
        {
          path: "addon/port-master/destination-work-column-edit/",
          name: "addon-port-master-destination-work-column-edit",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/destination-work-edit/EditColumnOrder"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "入出港予定表",
                url: { name: "addon-port-master-destination-order-list" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "編集",
                url: { name: "addon-port-master-destination-work-edit" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "項目順序変更",
                active: true,
              },
            ],
            pageTitle: "項目順序変更",
            portMasterNavigation: true,
          },
        },
        // 入出港一覧管理 入出港オーダー表
        {
          path: "addon/port-master/destination-order-list/",
          name: "addon-port-master-destination-order-list",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/destination-order-list/DestinationOrderList"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "入出港予定表",
                active: true,
              },
            ],
            pageTitle: "入出港予定表",
            portMasterNavigation: true,
          },
        },
        // 入出港一覧管理 入出港一括取り込み
        {
          path: "addon/port-master/destination-order-import/",
          name: "addon-port-master-destination-order-import",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/destination-order-import/DestinationOrderImport"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "入出港予定表",
                url: { name: "addon-port-master-destination-order-list" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "予定表一括取込",
                active: true,
              },
            ],
            pageTitle: "入出港予定表",
            portMasterNavigation: true,
          },
        },
        // 入出港一覧管理 一括取り込み予定表
        {
          path: "addon/port-master/destination-imported-work-edit",
          name: "addon-port-master-destination-imported-work-edit",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/destination-work-edit/DestinationImportedWorkEdit"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "入出港予定表",
                url: { name: "addon-port-master-destination-order-list" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "登録",
                active: true,
              },
            ],
            pageTitle: "入出港予定表",
            portMasterNavigation: true,
          },
        },

        // 水先案内人管理
        {
          path: "addon/port-master-pilot-master",
          name: "addon-port-master-pilot-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/pilot-master/PilotMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "水先案内人管理一覧",
                active: true,
              },
            ],
            pageTitle: "水先案内人管理",
            portMasterNavigation: true,
          },
        },
        // 水先案内人新規登録
        {
          path: "addon/port-master-pilot-insert",
          name: "addon-port-master-pilot-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/pilot-master/PilotInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "水先案内人管理一覧",
                url: { name: "addon-port-master-pilot-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "水先案内人管理",
            portMasterNavigation: true,
          },
        },

        // 班長管理
        {
          path: "addon/port-master-team-leader-master",
          name: "addon-port-master-team-leader-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/team-leader-master/TeamLeaderMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "班長管理一覧",
                active: true,
              },
            ],
            pageTitle: "班長管理",
            portMasterNavigation: true,
          },
        },
        // 班長新規登録
        {
          path: "addon/port-master-team-leader-insert",
          name: "addon-port-master-team-leader-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/team-leader-master/TeamLeaderInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "班長管理一覧",
                url: { name: "addon-port-master-team-leader-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "班長管理",
            portMasterNavigation: true,
          },
        },

        // タグボート管理
        {
          path: "addon/port-master-tug-boat-master",
          name: "addon-port-master-tug-boat-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/tug-boat-master/TugBoatMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "タグボート管理一覧",
                active: true,
              },
            ],
            pageTitle: "タグボート管理",
            portMasterNavigation: true,
          },
        },
        // タグボート新規登録
        {
          path: "addon/port-master-tug-boat-insert",
          name: "addon-port-master-tug-boat-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/tug-boat-master/TugBoatInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "タグボート管理一覧",
                url: { name: "addon-port-master-tug-boat-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "タグボート管理",
            portMasterNavigation: true,
          },
        },

        // 車両管理
        {
          path: "addon/port-master-vehicle-master",
          name: "addon-port-master-vehicle-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/vehicle-master/VehicleMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "車両管理一覧",
                active: true,
              },
            ],
            pageTitle: "車両管理",
            portMasterNavigation: true,
          },
        },
        // 車両新規登録
        {
          path: "addon/port-master-vehicle-insert",
          name: "addon-port-master-vehicle-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/vehicle-master/VehicleInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "車両管理一覧",
                url: { name: "addon-port-master-vehicle-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "車両管理",
            portMasterNavigation: true,
          },
        },

        // 綱取りボート管理
        {
          path: "addon/port-master-rope-picking-boat-master",
          name: "addon-port-master-rope-picking-boat-master",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/rope-picking-boat-master/RopePickingBoatMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "綱ボート管理一覧",
                active: true,
              },
            ],
            pageTitle: "綱ボート管理",
            portMasterNavigation: true,
          },
        },
        // 綱取りボート新規登録
        {
          path: "addon/port-master-rope-picking-boat-insert",
          name: "addon-port-master-rope-picking-boat-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/rope-picking-boat-master/RopePickingBoatInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "綱ボート管理一覧",
                url: { name: "addon-port-master-rope-picking-boat-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "綱ボート管理",
            portMasterNavigation: true,
          },
        },

        // タグ管理
        {
          path: "addon/port-master-tag-master",
          name: "addon-port-master-tag-master",
          component: () =>
            import("./views/enterprise/addon/port-master/tag-master/TagMaster"),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "タグ管理一覧",
                active: true,
              },
            ],
            pageTitle: "タグ管理",
            portMasterNavigation: true,
          },
        },
        // タグ新規登録
        {
          path: "addon/port-master-tag-insert",
          name: "addon-port-master-tag-insert",
          component: () =>
            import(
              "./views/enterprise/addon/port-master/tag-master/TagInsertData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "タグ管理一覧",
                url: { name: "addon-port-master-tag-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "新規登録",
                active: true,
              },
            ],
            pageTitle: "タグ管理",
            portMasterNavigation: true,
          },
        },
        /*=========================================================================================
          機能拡張 -> GkmgPortMaster
         ==========================================================================================*/
        // 仮設桟橋管理(Port Master C)
        {
          path: "addon/gkmg-port-master-port-menu",
          name: "addon-gkmg-port-master-port-menu",
          component: () =>
            import(
              "./views/enterprise/addon/gkmg-port-master/port-menu/PortMenu"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                active: true,
              },
            ],
            pageTitle: "入出港管理",
            gkmgPortMasterNavigation: true,
          },
        },

        // 船舶管理
        {
          path: "addon/gkmg-port-master-vessel-master",
          name: "addon-gkmg-port-master-vessel-master",
          component: () =>
            import(
              "./views/enterprise/addon/gkmg-port-master/vessel-master/VesselMaster"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-gkmg-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "船舶管理一覧",
                active: true,
              },
            ],
            pageTitle: "船舶管理",
            gkmgPortMasterNavigation: true,
          },
        },
        // 船舶情報
        {
          path: "addon/gkmg-port-master-vessel-view/:itemId",
          name: "addon-gkmg-port-master-vessel-view",
          component: () =>
            import(
              "./views/enterprise/addon/gkmg-port-master/vessel-master/VesselViewData"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-gkmg-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "船舶管理一覧",
                url: { name: "addon-gkmg-port-master-vessel-master" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "船舶情報",
                active: true,
              },
            ],
            pageTitle: "船舶情報",
            gkmgPortMasterNavigation: true,
          },
        },

        // 入出港一覧表示
        {
          path: "addon/gkmg-port-master/destination-work-display/",
          name: "addon-gkmg-port-master-destination-work-display",
          component: () =>
            import(
              "./views/enterprise/addon/gkmg-port-master/destination-work/DestinationWorkList"
            ),
          meta: {
            isPortMasterFullScreen: true,
          },
        },
        // 入出港一覧管理 入出港一括取り込み
        {
          path: "addon/gkmg-port-master/destination-order-import/",
          name: "addon-gkmg-port-master-destination-order-import",
          component: () =>
            import(
              "./views/enterprise/addon/gkmg-port-master/destination-order-import/DestinationOrderImport"
            ),
          meta: {
            breadcrumb: [
              {
                title: "入出港管理",
                url: { name: "addon-gkmg-port-master-port-menu" },
                active: false,
                color: "#2d9cdb",
              },
              {
                title: "予定表一括取込",
                active: true,
              },
            ],
            pageTitle: "一括取込み",
            gkmgPortMasterNavigation: true,
          },
        },
      ],
    },
    /*=========================================================================================
      エラー
     ==========================================================================================*/
    {
      path: "/error-404",
      name: "error-404",
      component: () => import("@/views/Error404.vue"),
    },
    {
      path: "/under-development",
      name: "under-development",
      component: () => import("@/views/UnderDevelopment.vue"),
    },
    // {
    //   path: '/pages/error-500',
    //   name: 'page-error-500',
    //   component: () => import('@/views/pages/Error500.vue'),
    //   meta: {
    //     rule: 'editor'
    //   }
    // },
  ],
});

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById("loading-bg");
  if (appLoading) {
    appLoading.style.display = "none";
  }
});

router.beforeEach((to, from, next) => {
  firebase.auth().onAuthStateChanged((user) => {
    if (user) {
      authService.hasUserInfoInLocalDb().then(async (res) => {
        if (res) {
          if (Store.state.auth.user === null) {
            authService.getAddons().then((res) => {
              Store.commit("SET_ADDONS", { addons: res });
            });
          }

          // User設定
          if (Store.state.auth.user === null) {
            authService.getUser().then((res) => {
              Store.commit("SET_USER", { user: res });
            });
          }

          // 有料・無料設定
          if (Store.state.auth.user === null) {
            const isFree = await authService.getIsFree();
            if (isFree !== null) Store.commit("SET_IS_FREE", { isFree });
          }

          // IDトークンリフレッシュ
          if (
            to.path === "/login/enterprise" ||
            from.path === "/login/enterprise" ||
            moment().valueOf() -
              moment(Store.state.auth.lastLoginSuccess).valueOf() <
              8 * 1000
          ) {
            // ログイン時とログイン直後は、moduleAuthActions が IDトークンの書き換えを行っているので、素通りさせる。
            return next();
          } else {
            authService
              .renewToken()
              .then(() => {
                return authService.getIdToken();
              })
              .then((t) => {
                let payload = {
                  idToken: t,
                };
                Store.commit("SET_ID_TOKEN", payload);
                // 無料ユーザが許可された画面以外に遷移しようとする場合、ログイン画面に遷移させる
                if (!Store.getters.isPaid && !isAllowPath(to.path)) {
                  return next({ name: "login-enterprise" });
                }
                return next();
              });
          }
        } else {
          // 無料ユーザが許可された画面以外に遷移しようとする場合、ログイン画面に遷移させる
          if (!Store.getters.isPaid && !isAllowPath(to.path)) {
            return next({ name: "login-enterprise" });
          }
          return next();
        }
      });
    } else {
      Vue.$log.info("== User Logout ==");
      Store.dispatch("auth/cleanupLocalData"); // データ削除する
      if (!to.path.startsWith("/login/enterprise")) {
        return next({ name: "login-enterprise", query: { logout: "force" } });
      }
      return next();
    }
  });
});

const isAllowPath = (path, allows = FREE_PATHS) => {
  return allows.some((ap) => path.startsWith(ap));
};

export default router;
